package ru.villawonka.app.presentation.screens.postCard.viewmodel

import ru.villawonka.app.AppConfig
import ru.villawonka.app.common.mvvm.BaseViewModel
import ru.villawonka.app.common.utils.fold
import ru.villawonka.app.domain.model.Deeplink
import ru.villawonka.app.domain.model.PostDomain
import ru.villawonka.app.domain.model.PostTypeDomain
import ru.villawonka.app.domain.model.getContactUsername
import ru.villawonka.app.domain.service.FavoritesService
import ru.villawonka.app.domain.service.UserSettingsService
import ru.villawonka.app.domain.usecase.RequestPostByIdUseCase
import ru.villawonka.app.platform.platform.Platform
import ru.villawonka.app.platform.platform.model.PlatformType
import ru.villawonka.app.presentation.model.PostUIModel
import ru.villawonka.app.presentation.model.toUIModel
import ru.villawonka.app.resources.AppResources

class PostCardViewModel(
    private val postId: Int,
    private val postType: PostTypeDomain,
    private val platform: Platform,
    private val userSettingsService: UserSettingsService,
    private val requestPostByIdUseCase: RequestPostByIdUseCase,
    private val favoritesService: FavoritesService,
) : BaseViewModel<PostCardUIState>(), UserSettingsService.Listener {

    private var cachedPostDomain: PostDomain? = null

    init {
        loadScreen()
        userSettingsService.addDelegate(this)
    }

    override fun onDispose() {
        super.onDispose()
        userSettingsService.removeDelegate(this)
    }

    override fun createInitialState(): PostCardUIState = PostCardUIState.Loading

    fun onChatClick() = launch {
        when (val post = cachedPostDomain) {
            is PostDomain.Accommodation -> post.getContactUsername()?.let {
                shootEvent(PostCardUIEvent.OpenExternalLink(
                    link = Deeplink.Telegram.chat(
                        username = it,
                        text = AppResources.strings.helloMessage(
                            deeplink = Deeplink.Telegram.post(
                                postType = PostTypeDomain.ACCOMMODATION,
                                miniAppUrl = AppConfig.miniAppUrl,
                                postId = post.id
                            )
                        )
                    )
                ))
            }
        }
    }

    fun onLikeClick(isLiked: Boolean) = launch {
        cachedPostDomain?.let {
            favoritesService.toggleLike(postId, postType, isLiked)
        }
    }

    fun onShareClick() = launch {
        (currentStateValue as? PostCardUIState.Data)?.let { state ->
            val event = when (platform.type) {
                PlatformType.TELEGRAM -> when (val post = state.post) {
                    is PostUIModel.Accommodation -> {
                        val text = "${post.accommodationType.value} - " +
                                "${post.displayCost} ${post.displayCurrency.code} - " +
                                "${post.rentType.value}!"

                        val postLink = Deeplink.Telegram.post(
                            postType = PostTypeDomain.ACCOMMODATION,
                            miniAppUrl = AppConfig.miniAppUrl,
                            postId = post.id
                        )
                        val shareLink = Deeplink.Telegram.share(
                            url = postLink,
                            text = text
                        )
                        PostCardUIEvent.OpenExternalLink(shareLink)
                    }
                }
                PlatformType.IOS,
                PlatformType.ANDROID -> throw NotImplementedError()
            }
            shootEvent(event)
        }
    }

    fun onImageClick(selectedIndex: Int) = launch {
        cachedPostDomain?.let {
            shootEvent(PostCardUIEvent.OpenPhotoSliderScreen(
                initialPage = selectedIndex,
                photos = it.media
            ))
        }
    }

    fun onSendReportClick() = launch {
        cachedPostDomain?.let {
            shootEvent(PostCardUIEvent.OpenSendReportScreen(it))
        }
    }

    fun onConversationDetailsClick() = launch {
        (currentStateValue as? PostCardUIState.Data)?.let { state ->
            when (val post = state.post) {
                is PostUIModel.Accommodation -> post.conversationDetails?.let {
                    shootEvent(PostCardUIEvent.OpenConversationDetailsScreen(it))
                }
            }
        }
    }

    fun onGoToSourceClick() = launch {
        cachedPostDomain?.source?.let {
            shootEvent(PostCardUIEvent.OpenExternalLink(it.postUrl))
        }
    }

    fun onRetryLoadClick() = launch { loadScreen() }

    private fun loadScreen() = launch {
        setState(PostCardUIState.Loading)
        requestPostByIdUseCase(postId, postType).fold(
            ifLeft = {
                setState(PostCardUIState.Error(it))
            },
            ifRight = {
                setState(PostCardUIState.Data(
                    post = it.toUIModel(),
                    sendMessageButtonVisible = it.getContactUsername() != null
                ))
                cachedPostDomain = it
            }
        )
    }

    override fun onSettingsChanged() { loadScreen() }
}